package com.eladfinish.bible.contest.screens.completeVerse.menu

import androidx.compose.runtime.Composable
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.help
import com.eladfinish.bible.contest.ic_auto_renew
import com.eladfinish.bible.contest.ic_help
import com.eladfinish.bible.contest.missing_words_3_dots
import com.eladfinish.bible.contest.missing_words_underline
import com.eladfinish.bible.contest.new_verse
import com.eladfinish.bible.contest.show_beginning_of_verse
import com.eladfinish.bible.contest.show_end_of_verse
import com.eladfinish.bible.contest.show_words_partially
import com.eladfinish.bible.contest.ui.components.toolbar.SetToolbarConfiguration
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.SubMenuContent
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarActionConfig
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarActionPlacement
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarConfiguration
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarHeaderComponentConfig
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarIcon
import com.eladfinish.bible.contest.ui.components.toolbar.model.ToolbarKind
import com.eladfinish.bible.contest.ui.menus.actions.CompleteVerseToolbarAction
import com.eladfinish.bible.contest.ui.menus.actions.ToolbarActionEmitter
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.desc.desc
import kotlinx.collections.immutable.persistentListOf

enum class CompleteVerseToolbarId {
    NEW_VERSE,
    HELP,

    UNDERLINE_MODE,
    THREE_DOTS_MODE,
}

enum class UnderlineOption {
    SHOW_BEGINNING,
    SHOW_END,
    HIDE_SOME_WORDS,
}

enum class ThreeDotsOption {
    SHOW_BEGINNING,
    SHOW_END,
}

@Composable
internal fun CompleteVerseMenu(
    title: String,
    toolbarEmitter: ToolbarActionEmitter<CompleteVerseToolbarAction>,
    underlineSelected: UnderlineOption,
    threeDotsSelected: ThreeDotsOption,
) {
    SetToolbarConfiguration(
        configuration = ToolbarConfiguration(
            kind = ToolbarKind.CompleteVerse,
            title = ToolbarHeaderComponentConfig.Text(text = title),
            actions = persistentListOf(
                ToolbarActionConfig.IconAction(
                    id = CompleteVerseToolbarId.NEW_VERSE,
                    icon = ToolbarIcon.PainterIcon(painterResource(SharedRes.images.ic_auto_renew)),
                    menuText = SharedRes.strings.new_verse.desc(),
                    onClick = { toolbarEmitter.emit(action = CompleteVerseToolbarAction.NewVerse) },
                    placement = ToolbarActionPlacement.OnToolbar,
                ),
                ToolbarActionConfig.IconAction(
                    id = CompleteVerseToolbarId.HELP,
                    icon = ToolbarIcon.PainterIcon(painterResource(SharedRes.images.ic_help)),
                    menuText = SharedRes.strings.help.desc(),
                    onClick = { toolbarEmitter.emit(action = CompleteVerseToolbarAction.Help) },
                    placement = ToolbarActionPlacement.OnToolbar,
                ),

                // --- Overflow: Radio submenu 1 (Underline) ---
                ToolbarActionConfig.SubMenu(
                    id = CompleteVerseToolbarId.UNDERLINE_MODE,
                    stringDesc = SharedRes.strings.missing_words_underline.desc(),
                    placement = ToolbarActionPlacement.InOverflow,
                    content = SubMenuContent.RadioSubMenu(
                        id = CompleteVerseToolbarId.UNDERLINE_MODE,
                        selected = underlineSelected,
                        options = listOf(
                            SubMenuContent.RadioOption(
                                option = UnderlineOption.SHOW_BEGINNING,
                                title = SharedRes.strings.show_beginning_of_verse.desc(),
                            ),
                            SubMenuContent.RadioOption(
                                option = UnderlineOption.SHOW_END,
                                title = SharedRes.strings.show_end_of_verse.desc(),
                            ),
                            SubMenuContent.RadioOption(
                                option = UnderlineOption.HIDE_SOME_WORDS,
                                title = SharedRes.strings.show_words_partially.desc(),
                            ),
                        ),
                        onOptionSelected = { option ->
                            when (option) {
                                UnderlineOption.SHOW_BEGINNING ->
                                    toolbarEmitter.emit(
                                        CompleteVerseToolbarAction.UnderlineMode(
                                            underlineOption = UnderlineOption.SHOW_BEGINNING
                                        )
                                    )

                                UnderlineOption.SHOW_END ->
                                    toolbarEmitter.emit(
                                        CompleteVerseToolbarAction.UnderlineMode(
                                            underlineOption = UnderlineOption.SHOW_END
                                        )
                                    )

                                UnderlineOption.HIDE_SOME_WORDS ->
                                    toolbarEmitter.emit(
                                        CompleteVerseToolbarAction.UnderlineMode(
                                            underlineOption = UnderlineOption.HIDE_SOME_WORDS
                                        )
                                    )
                            }
                        },
                    ),
                ),

                // --- Overflow: Radio submenu 2 (Three dots) ---
                ToolbarActionConfig.SubMenu(
                    id = CompleteVerseToolbarId.THREE_DOTS_MODE,
                    stringDesc = SharedRes.strings.missing_words_3_dots.desc(),
                    placement = ToolbarActionPlacement.InOverflow,
                    content = SubMenuContent.RadioSubMenu(
                        id = CompleteVerseToolbarId.THREE_DOTS_MODE,
                        selected = threeDotsSelected,
                        options = listOf(
                            SubMenuContent.RadioOption(
                                option = ThreeDotsOption.SHOW_BEGINNING,
                                title = SharedRes.strings.show_beginning_of_verse.desc(),
                            ),
                            SubMenuContent.RadioOption(
                                option = ThreeDotsOption.SHOW_END,
                                title = SharedRes.strings.show_end_of_verse.desc(),
                            ),
                        ),
                        onOptionSelected = { option ->
                            when (option) {
                                ThreeDotsOption.SHOW_BEGINNING ->
                                    toolbarEmitter.emit(
                                        CompleteVerseToolbarAction.ThreeDotsMode(
                                            threeDotsOption = ThreeDotsOption.SHOW_BEGINNING
                                        )
                                    )

                                ThreeDotsOption.SHOW_END ->
                                    toolbarEmitter.emit(
                                        CompleteVerseToolbarAction.ThreeDotsMode(
                                            threeDotsOption = ThreeDotsOption.SHOW_END
                                        )
                                    )
                            }
                        },
                    ),
                ),
            )
        )
    )
}
